"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.projectAppBaseDocHtml = projectAppBaseDocHtml;
/** Pure literal Doc projection. Authorization/materialization happens upstream. */
function projectAppBaseDocHtml(input, urlIssue, mediaUrl, docUrl) {
    const fail = () => {
        throw new Error('Invalid App rich document');
    };
    if (!input || typeof input !== 'object')
        return fail();
    if (new TextEncoder().encode(JSON.stringify(input)).length > 2 * 1024 * 1024)
        return fail();
    const reference = input;
    if (typeof reference.resolved === 'boolean' &&
        typeof reference.docId === 'string') {
        if (!reference.resolved)
            return '';
        if (!reference.document)
            return `<p>${String(reference.content ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')}</p>`;
        input = reference.document;
    }
    const doc = input;
    if (doc.contentType !== 'doc_page' ||
        doc.snapshotVersion !== 1 ||
        !doc.nodes ||
        typeof doc.rootNodeId !== 'string')
        return fail();
    const escape = (value) => value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    const tags = new Set([
        'article',
        'section',
        'p',
        'div',
        'span',
        'br',
        'hr',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'ul',
        'ol',
        'li',
        'blockquote',
        'pre',
        'code',
        'strong',
        'em',
        'u',
        's',
        'a',
        'img',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
    ]);
    const visiting = new Set();
    let count = 0;
    let emitted = 0;
    const charge = (value) => {
        emitted += new TextEncoder().encode(value).length;
        if (emitted > 1024 * 1024)
            return fail();
        return value;
    };
    const literal = (formula) => {
        if (!formula ||
            typeof formula !== 'object' ||
            !('type' in formula) ||
            formula.type !== 'value' ||
            !('value' in formula))
            return fail();
        return formula.value;
    };
    const visit = (id, depth) => {
        if (++count > 10000 ||
            depth > 64 ||
            visiting.has(id) ||
            !Object.prototype.hasOwnProperty.call(doc.nodes, id))
            return fail();
        visiting.add(id);
        const node = doc.nodes[id];
        if (('repeat' in node && node.repeat) ||
            ('repeatKey' in node && node.repeatKey) ||
            node.customProperties)
            return fail();
        if (node.condition && !literal(node.condition)) {
            visiting.delete(id);
            return '';
        }
        if (node.type === 'text') {
            const value = literal(node.value);
            visiting.delete(id);
            return typeof value === 'string' ? charge(escape(value)) : fail();
        }
        if (!Array.isArray(node.children))
            return fail();
        // A frame is the video block of a document. It is left out together with
        // its attributes and everything inside it; any other unknown tag is
        // refused below. The tag stays a literal here: the text of this function
        // is embedded in page scripts without its module.
        if (node.type === 'element' && node.tag === 'iframe') {
            visiting.delete(id);
            return '';
        }
        const content = node.children
            .map(child => visit(child, depth + 1))
            .join('');
        visiting.delete(id);
        if (node.type === 'slot')
            return content;
        if (node.type !== 'element' || !tags.has(node.tag))
            return fail();
        const sourceId = node.attrs['data-source-id'];
        const attributes = { ...node.attrs };
        if (sourceId) {
            const id = literal(sourceId);
            if (typeof id !== 'string' ||
                !id ||
                !['img', 'a'].includes(node.tag) ||
                !mediaUrl)
                return fail();
            delete attributes['data-source-id'];
            attributes[node.tag === 'img' ? 'src' : 'href'] = {
                type: 'value',
                value: mediaUrl(id),
            };
        }
        // Unpublished/unavailable/ambiguous targets keep their label without a link.
        if (attributes['data-doc-id']) {
            const id = literal(attributes['data-doc-id']);
            if (typeof id !== 'string')
                return fail();
            const href = docUrl?.(id);
            delete attributes['data-doc-id'];
            delete attributes.href;
            if (href)
                attributes.href = { type: 'value', value: href };
        }
        const attrs = Object.entries(attributes)
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
            .map(([name, formula]) => {
            if (![
                'href',
                'src',
                'alt',
                'title',
                'colspan',
                'rowspan',
                'width',
                'height',
                'data-block-type',
                'data-list-type',
                'data-checked',
                'data-language',
                'data-caption',
                'data-database-title',
                'data-mime-type',
            ].includes(name))
                return fail();
            const value = literal(formula);
            if (value === null)
                return '';
            if (!['string', 'number', 'boolean'].includes(typeof value))
                return fail();
            if ((name === 'href' || name === 'src') &&
                urlIssue(String(value), node.tag === 'a' && name === 'href' ? 'link' : 'resource'))
                return fail();
            return charge(` ${name}="${escape(String(value))}"`);
        })
            .join('');
        return ['br', 'hr', 'img'].includes(node.tag)
            ? `<${node.tag}${attrs}>`
            : `<${node.tag}${attrs}>${content}</${node.tag}>`;
    };
    const html = visit(doc.rootNodeId, 0);
    if (new TextEncoder().encode(html).length > 1024 * 1024)
        return fail();
    return html;
}
